import React, { useLayoutEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { HomeOutlined as HomeOutlinedIcon, ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import { pageToolbarSlot, pageCrumbTailSlot, usePageHomeHandler } from "../../helper/pageToolbar";

// ════════════════════════════════════════════════════════════════════════
//  Cream/gold admin theme — the look of the admin Account and Settings
//  pages, packaged for reuse (Integrations page). Pure styling helpers: no
//  state, no data. Form fields use GoldField (./GoldField) alongside these.
// ════════════════════════════════════════════════════════════════════════

export const GOLD_THEME = {
  pageBg: "#f6f1e6",
  surface: "#fffcf7",
  field: "#fffaf0",
  text: "#1d1a14",
  textSub: "#4a4234",
  muted: "#6b6459",
  border: "#ece2cc",
  borderSoft: "#efe6d3",
  pill: "#d9c79e",
  gold: "#b8892c",
  goldLight: "#d8b25a",
  goldDark: "#7a5712",
  goldSoft: "rgba(184,137,44,0.12)",
  gradient: "linear-gradient(180deg, #8c6a1c, #6f5212)",
  gradientHover: "linear-gradient(180deg, #9a7621, #7a5a14)",
};

// Full-bleed cream page — cancels AdminLayout's own Container padding so the
// background fills the whole content area (AdminLayout itself is untouched).
export const GOLD_PAGE_SX = {
  position: "relative", overflow: "hidden",
  mt: { xs: "-24px", sm: "-40px", md: "-64px" },
  mb: { xs: "-24px", sm: "-40px", md: "-64px" },
  mx: { xs: "-12px", sm: "-24px" },
  minHeight: "calc(100vh - 72px)",
  p: { xs: 2, sm: 3, md: 4 },
  background: `radial-gradient(ellipse at 85% 0%, rgba(216,178,90,0.16), transparent 55%), ${GOLD_THEME.pageBg}`,
  // Wide content (e.g. a minWidth:1250 orders table inside its own scroll
  // box) must not stretch AdminLayout's flex <main> past the viewport —
  // the page takes the layout's width and inner tables scroll instead.
  contain: "inline-size",
  // position only (no z-index): content still paints above the swoosh,
  // without each section becoming its own stacking layer — otherwise a
  // later section would cover an earlier section's open dropdown.
  "& > *": { position: "relative" },
};

// Cream card surface (Account page's glassCard).
export const GOLD_CARD_SX = {
  bgcolor: GOLD_THEME.surface,
  border: `1px solid ${GOLD_THEME.borderSoft}`,
  borderRadius: "16px",
  boxShadow: "0 8px 28px rgba(122,87,18,0.07)",
};

// Gold gradient primary button (MUI Button / GradientActionButton `sx`).
export const GOLD_BTN_SX = {
  background: GOLD_THEME.gradient,
  color: "#fff",
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 700,
  boxShadow: "0 4px 14px rgba(111,82,18,0.30)",
  "&:hover": { background: GOLD_THEME.gradientHover, boxShadow: "0 8px 22px rgba(111,82,18,0.38)" },
  "&.Mui-disabled": { background: GOLD_THEME.gradient, color: "rgba(255,255,255,0.8)", opacity: 0.55 },
};

// Cream outlined secondary button.
export const GOLD_OUTLINED_BTN_SX = {
  border: `1px solid ${GOLD_THEME.pill}`,
  color: GOLD_THEME.goldDark,
  background: GOLD_THEME.field,
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 600,
  "&:hover": { background: "#fdf1d8", borderColor: GOLD_THEME.gold },
};

// SearchBar (shared/SearchBar.jsx) recolor via its `sx` prop.
export const GOLD_SEARCH_SX = {
  border: `1px solid ${GOLD_THEME.pill}`, bgcolor: GOLD_THEME.field,
  "& > div": { bgcolor: `${GOLD_THEME.field} !important`, borderColor: "#e3d6b8 !important" },
  "& input": { color: `${GOLD_THEME.text} !important` },
  "& input::placeholder": { color: "#7d7466 !important", opacity: "1 !important" },
  "& .MuiSvgIcon-root": { color: `${GOLD_THEME.goldDark} !important` },
  "&:focus-within": { borderColor: GOLD_THEME.gold, boxShadow: "0 0 0 3px rgba(184,137,44,0.15)" },
};

// Page-level cascade for content built from shared pieces (EntityCard
// `.ms-glow` Papers, SearchBar, MUI tables/checkboxes) — spread next to
// GOLD_PAGE_SX on a page root so those pick up the cream/gold look too.
export const GOLD_CONTENT_SX = {
  "& .ms-search-bar": GOLD_SEARCH_SX,
  "& .MuiPaper-root.ms-glow": {
    backgroundColor: `${GOLD_THEME.surface} !important`,
    borderColor: `${GOLD_THEME.borderSoft} !important`,
    boxShadow: "0 8px 28px rgba(122,87,18,0.07) !important",
  },
  "& .MuiTableHead-root .MuiTableCell-root": {
    backgroundColor: "#f6efe1 !important", color: `${GOLD_THEME.text} !important`, fontWeight: "700 !important",
  },
  "& .MuiTableCell-root": { borderColor: `${GOLD_THEME.borderSoft} !important` },
  "& .MuiTableBody-root .MuiTableRow-root:hover": { backgroundColor: "rgba(184,137,44,0.05) !important" },
  "& .MuiOutlinedInput-root.Mui-focused::before": { display: "none !important" },
  "& .MuiCheckbox-root.Mui-checked, & .MuiCheckbox-root.MuiCheckbox-indeterminate": { color: `${GOLD_THEME.gold} !important` },
};

// MUI <Dialog PaperProps={{ sx: GOLD_DIALOG_PAPER_SX }}> — cream dialog with
// gold title/actions. Dialogs are portaled, so page-level styles can't reach them.
export const GOLD_DIALOG_PAPER_SX = {
  background: GOLD_THEME.surface,
  border: `1px solid ${GOLD_THEME.border}`,
  borderRadius: "16px",
  boxShadow: "0 24px 64px rgba(122,87,18,0.20)",
  color: GOLD_THEME.text,
  "& .MuiDialogTitle-root": { fontWeight: 800, color: GOLD_THEME.text },
  "& .MuiDialogActions-root": { px: 3, pb: 2.5, gap: 1 },
  "& .MuiCheckbox-root.Mui-checked": { color: `${GOLD_THEME.gold} !important` },
  "& .MuiOutlinedInput-root.Mui-focused::before": { display: "none !important" },
};

// Gold "swoosh" in the page's top-right corner.
export const GoldSwoosh = ({ id = "goldSwoosh" }) => (
  <Box
    component="svg"
    viewBox="0 0 480 170"
    preserveAspectRatio="none"
    aria-hidden
    sx={{ position: "absolute !important", top: 0, right: 0, width: { xs: 260, md: 480 }, height: { xs: 92, md: 170 }, pointerEvents: "none", zIndex: "0 !important" }}
  >
    <defs>
      <linearGradient id={`${id}A`} x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#e9c977" stopOpacity="0" />
        <stop offset="55%" stopColor="#dcb258" stopOpacity="0.85" />
        <stop offset="100%" stopColor="#c99a35" stopOpacity="1" />
      </linearGradient>
      <linearGradient id={`${id}B`} x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#f3dfae" stopOpacity="0" />
        <stop offset="100%" stopColor="#ecd08a" stopOpacity="0.55" />
      </linearGradient>
    </defs>
    <path d="M90 170 C 210 120, 300 70, 480 8 L 480 40 C 330 92, 230 135, 150 170 Z" fill={`url(#${id}B)`} />
    <path d="M60 170 C 200 110, 310 50, 480 0 L 480 14 C 320 62, 215 118, 96 170 Z" fill={`url(#${id}A)`} />
  </Box>
);

// Breadcrumb + big title + subtitle, with an optional right-side slot.
// Small "🏠 Section › Page" breadcrumb row shown at the top of every admin
// page (on its own, or as the first line of GoldPageHeader). One line on
// every screen — long names shorten with "…".
export const GoldCrumbs = ({ crumbs = [], sx = {}, after = null }) => {
  // Right-hand slot of the row: a page's Sync Live / Refresh / Time-filter bar is drawn here
  // (see PageToolbar), a little smaller, instead of in a row of its own below.
  const [slotEl, setSlotEl] = useState(null);
  useLayoutEffect(() => {
    if (!slotEl) return undefined;
    pageToolbarSlot.register(slotEl);
    return () => pageToolbarSlot.unregister(slotEl);
  }, [slotEl]);
  // Slot straight after the crumbs (a page's Company ▾ › Store ▾ switchers — see PageCrumbTail) + a clickable Home.
  const [tailEl, setTailEl] = useState(null);
  useLayoutEffect(() => {
    if (!tailEl) return undefined;
    pageCrumbTailSlot.register(tailEl);
    return () => pageCrumbTailSlot.unregister(tailEl);
  }, [tailEl]);
  const homeClick = usePageHomeHandler();
  return (
    <Box sx={{ position: "relative", zIndex: 1, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, flexWrap: "wrap", ...sx }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, minWidth: 0, flexWrap: "nowrap", flex: "0 1 auto" }}>
        <HomeOutlinedIcon onClick={homeClick || undefined} titleAccess={homeClick ? "Back to all companies" : undefined}
          sx={{ fontSize: 17, color: GOLD_THEME.text, flexShrink: 0, ...(homeClick ? { cursor: "pointer", "&:hover": { color: "#b8892c" } } : {}) }} />
        {crumbs.map((c, i) => (
          <React.Fragment key={`${c}-${i}`}>
            {i > 0 && <ChevronRightIcon sx={{ fontSize: 16, color: GOLD_THEME.textSub, flexShrink: 0 }} />}
            {/* While a page has registered a "go back" handler (inside a company on the Financials pages), the page's own
                name — the last crumb — is a link back to the first step, same as the Home icon. */}
            <Typography
              onClick={homeClick && i === crumbs.length - 1 ? homeClick : undefined}
              title={homeClick && i === crumbs.length - 1 ? "Back to all companies" : undefined}
              sx={{
                fontSize: "0.8rem", color: GOLD_THEME.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0,
                ...(homeClick && i === crumbs.length - 1 ? { cursor: "pointer", fontWeight: 700, color: GOLD_THEME.gold, "&:hover": { textDecoration: "underline" } } : {}),
              }}>{c}</Typography>
          </React.Fragment>
        ))}
        {after && (<><ChevronRightIcon sx={{ fontSize: 16, color: GOLD_THEME.textSub, flexShrink: 0 }} />{after}</>)}
        <Box ref={setTailEl} sx={{ display: "flex", alignItems: "center", gap: 0.6, minWidth: 0, flex: "0 1 auto", "&:empty": { display: "none" } }} />
      </Box>
      <Box ref={setSlotEl} sx={{
        display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap", minWidth: 0,
        flex: { xs: "1 1 100%", md: "0 1 auto" }, justifyContent: { xs: "flex-start", md: "flex-end" }, ml: { md: "auto" },
        zoom: 0.85, // a little smaller than the buttons used to be
        "&:empty": { display: "none" },
        "& > *": { mb: "0 !important", mt: "0 !important" },
      }} />
    </Box>
  );
};

export const GoldPageHeader = ({ crumbs = [], title, subtitle, right }) => (
  <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "flex-start", sm: "flex-end" }, justifyContent: "space-between", gap: 2, mb: 3 }}>
    <Box>
      <GoldCrumbs crumbs={crumbs} />
      <Typography sx={{ mt: 1.8, fontSize: { xs: "1.9rem", md: "2.3rem" }, fontWeight: 800, color: GOLD_THEME.text, letterSpacing: "-0.03em", lineHeight: 1.05 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{ fontSize: "0.85rem", color: GOLD_THEME.textSub, mt: 0.5 }}>{subtitle}</Typography>
      )}
    </Box>
    {right}
  </Box>
);

// Gold pill tab group — "main" = Account page's tab bar, "sub" = its rounded
// sub-tab pills. tabs: [{ key, label, Icon?, count? }]; onSelect gets the tab object.
//   Icon   a component rendered as <Icon size weight />
//   count  a small number after the label (e.g. how many items the tab would show); a tab whose
//          count is 0 is dimmed until it is selected
// "main" tabs on phones: ONE row, every tab an equal share of the width (a long label wraps inside its
// own tab instead of pushing a tab down). With many tabs, pass phoneWrap to let them flow onto a
// second row on phones instead, and compact to keep them on ONE row everywhere else: no minimum tab
// width and tighter padding, so six tabs fit side by side (a bar still too wide for its space scrolls
// sideways instead of wrapping).
// small: a smaller pill (less padding, smaller text and icon) — the tab rows that sit in the first row / hang off a tab.
export const GoldPillTabs = ({ tabs, active, onSelect, variant = "sub", phoneWrap = false, compact = false, nowrap = false, small = false }) => {
  const main = variant === "main";
  const wrapPhones = main && phoneWrap;
  const oneRow = main && compact;
  return (
    <Box role="tablist" sx={{
      display: main ? { xs: "flex", sm: "inline-flex" } : "inline-flex",
      // nowrap (sub bars only): pills stay on ONE row from md up — GoldSubTabRow relies on that to measure the bar —
      // and still wrap on phones.
      flexWrap: main ? { xs: wrapPhones ? "wrap" : "nowrap", sm: oneRow ? "nowrap" : "wrap" } : (nowrap ? { xs: "wrap", md: "nowrap" } : "wrap"),
      overflowX: oneRow ? { sm: "auto" } : "visible",
      width: main ? { xs: "100%", sm: "auto" } : "auto",
      gap: small ? 0.25 : 0.4, p: small ? 0.25 : 0.4, maxWidth: "100%", boxSizing: "border-box",
      border: `1px solid ${GOLD_THEME.pill}`, borderRadius: main ? "12px" : "999px", background: GOLD_THEME.field,
    }}>
      {tabs.map((tab) => {
        const isActive = active === tab.key;
        const Icon = tab.Icon;
        return (
          <Box
            key={tab.key}
            data-tab-key={tab.key}
            role="tab"
            aria-selected={isActive}
            tabIndex={0}
            onClick={() => onSelect(tab)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(tab); } }}
            sx={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: small ? 0.5 : 0.8,
              minWidth: main ? { xs: 0, sm: oneRow ? 0 : 120, md: oneRow ? 0 : 150 } : 0,
              flex: main ? { xs: wrapPhones ? "1 1 30%" : "1 1 0", sm: "0 0 auto" } : "0 0 auto",
              px: small ? { xs: 0.9, sm: 1.2 } : (main ? { xs: 0.8, sm: oneRow ? 1.8 : 2.5 } : 2.2), py: small ? 0.25 : (main ? { xs: 0.7, sm: 0.9 } : 0.8),
              borderRadius: main ? "10px" : "999px", cursor: "pointer", userSelect: "none", transition: "all 0.2s",
              background: isActive ? GOLD_THEME.gradient : "transparent",
              boxShadow: isActive ? "0 4px 12px rgba(111,82,18,0.30)" : "none",
              color: isActive ? "#ffffff" : (tab.count === 0 ? GOLD_THEME.muted : GOLD_THEME.text),
              "&:hover": { background: isActive ? undefined : "rgba(184,137,44,0.08)" },
              "&:focus-visible": { outline: `2px solid ${GOLD_THEME.gold}`, outlineOffset: 1 },
            }}
          >
            {Icon && <Icon size={small ? 13 : 16} weight={isActive ? "fill" : "regular"} />}
            <Typography sx={{
              fontSize: small ? "0.7rem" : (main ? { xs: "0.72rem", sm: "0.82rem" } : "0.8rem"), fontWeight: 600, color: "inherit",
              whiteSpace: main ? { xs: "normal", sm: "nowrap" } : "nowrap",
              textAlign: "center", lineHeight: main ? { xs: 1.2, sm: 1.5 } : 1.5,
            }}>
              {tab.label}
            </Typography>
            {tab.count !== undefined && tab.count !== null && (
              <Typography component="span" sx={{ fontSize: "0.72rem", fontWeight: 700, color: "inherit", opacity: 0.85 }}>{tab.count}</Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

// A second row of tabs that hangs off the ACTIVE tab of a main GoldPillTabs bar — the same L-shaped gold
// arrow Settings uses between "Master Settings" and its Modules/Permissions/… row. Put the main bar
// inside an element you hold a ref to (`parentRef`), pass the active main tab's key as `parentKey`, and
// render the sub-row's <GoldPillTabs nowrap> as the children. The row starts where the active tab ends and the
// arrow is drawn on desktop/tablet only; on phones it is just the row, with no padding or arrow.
// When the active tab is far to the right and the pills would not fit beside it, the row starts further left
// (never before the tab's own centre line) and the arrow simply points at wherever it starts.
// The main bar's tabs must carry data-tab-key (the shared GoldPillTabs does).
// rise: px the arrow starts ABOVE this row — when the main tabs sit in the page's first (breadcrumb) row there is a
// gap between that row and this one, and the arrow still has to start right under the active tab.
// under: the sub-tabs start right UNDER the active tab (its left edge) and a short straight arrow points down at them,
// instead of the L-shaped arrow that runs out to the tab's right — saves the row's width.
export const GoldSubTabRow = ({ parentRef, parentKey, children, sx = {}, rise = 0, under = false }) => {
  const rowRef = useRef(null);
  const measureRef = useRef(null);
  const [box, setBox] = useState(null); // { center, start } relative to the row's left edge: the tab's centre line, where the pills begin
  useLayoutEffect(() => {
    const parent = parentRef && parentRef.current;
    if (!parent) { setBox(null); measureRef.current = null; return undefined; }
    const measure = () => {
      const row = rowRef.current;
      const el = parent.querySelector(`[data-tab-key="${parentKey}"]`);
      if (!row || !el) { setBox(null); return; }
      const r0 = row.getBoundingClientRect();
      const r = el.getBoundingClientRect();
      const right = r.right - r0.left;
      const center = r.left - r0.left + r.width / 2;
      let start = under ? Math.max(0, r.left - r0.left) : right + 14;
      const bar = row.querySelector('[role="tablist"]');
      const barWidth = bar ? bar.scrollWidth : 0; // the pills' natural width (the bar does not wrap on desktop)
      if (!under && barWidth && start + barWidth > row.clientWidth) start = Math.max(center + 24, row.clientWidth - barWidth);
      const next = { center: Math.round(center), start: Math.round(start) };
      setBox((prev) => (prev && prev.center === next.center && prev.start === next.start ? prev : next));
    };
    measureRef.current = measure;
    measure();
    window.addEventListener("resize", measure);
    // Tabs can change width without a window resize (fonts loading, a tab appearing for some users).
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (observer) observer.observe(parent);
    return () => { window.removeEventListener("resize", measure); if (observer) observer.disconnect(); measureRef.current = null; };
  }, [parentRef, parentKey, under]);
  // The pills themselves can change (another tab brings another set): measure again after every render. It only
  // updates state when a number actually changed, so this settles at once.
  useLayoutEffect(() => { if (measureRef.current) measureRef.current(); });

  return (
    <Box ref={rowRef} sx={{
      position: "relative",
      pt: { xs: 0, md: 1.2 },
      pl: { xs: 0, md: box ? `${box.start}px` : 0 },
      ...sx,
    }}>
      {box && under && (
        <Box aria-hidden sx={{
          display: { xs: "none", md: "block" }, position: "absolute", pointerEvents: "none",
          left: `${box.center}px`, top: -4 - rise, height: `${rise + 10}px`, borderLeft: "2px solid #c99a35",
          "&::after": {
            content: '""', position: "absolute", left: -6, bottom: -6,
            borderLeft: "5px solid transparent", borderRight: "5px solid transparent", borderTop: "8px solid #c99a35",
          },
        }} />
      )}
      {box && !under && (
        <Box aria-hidden sx={{
          display: { xs: "none", md: "block" }, position: "absolute", pointerEvents: "none",
          left: `${box.center}px`, top: -4 - rise,
          width: `${Math.max(0, box.start - 6 - box.center)}px`,
          height: `calc(50% + ${10 + rise}px)`,
          borderLeft: "2px solid #c99a35", borderBottom: "2px solid #c99a35", borderBottomLeftRadius: "12px",
          "&::after": {
            content: '""', position: "absolute", right: -2, bottom: -6,
            borderTop: "5px solid transparent", borderBottom: "5px solid transparent", borderLeft: "8px solid #c99a35",
          },
        }} />
      )}
      {children}
    </Box>
  );
};
